import type { SpendMonth, TrendPoint } from '@/components/ui/charts';
import { ZONE_LABEL, zoneOf, type Thresholds } from '@/components/ui/charts';
import type { StatusChipTone } from '@/components/ui/status-chip';
import type { HrCriterion, HrDomain, HrSuggestedLink } from '@/lib/api/health-readiness';
import type { DomainRating } from '@/lib/api/health-domains';
import { shortMonthLabel, type BudgetSeriesMonth } from '@/lib/api/health-budget';
import type { ScheduleMilestone, UpdateMilestoneBody } from '@/lib/api/health-schedule';
import type { KeptLink } from '@/lib/api/readiness-scorecard';
import type { CrossModuleReferenceItem } from '@/lib/api/readiness-xref';
import { dateValue, formatDate } from '@/lib/utils/format';
import { POSTURE_LABEL } from './model';
import { localDayIso } from './overview/overview-model';

/**
 * What the Domains and Go-live criteria details draw (T-0243 / E22-F02),
 * read off the composite read and the module reads. Pure, so the panes
 * stay markup and these stay tested.
 *
 * Integrity rule (E22 design §6.3): no rollup in the browser. Totals,
 * contributions, slips, projections and the spend series are the API's.
 * What happens here is selection, renaming, a calendar month compared to
 * today, a finding's age in days, and which fields of a form changed.
 */

const DAY = 86_400_000;

// ---------------------------------------------------------------------------
// Budget
// ---------------------------------------------------------------------------

/**
 * The spend curve's months from the API's cumulative series: actuals up to
 * this month, the API's projection after it (the curve draws the dashed
 * line on from the last actual).
 */
export function spendMonths(series: ReadonlyArray<BudgetSeriesMonth>, today: Date): SpendMonth[] {
  const current = localDayIso(today).slice(0, 7);
  return series.map((p) => ({
    label: shortMonthLabel(p.month),
    plan: p.plan,
    actual: p.month <= current ? p.actual : null,
    projected: p.month > current ? p.projected : null,
  }));
}

// ---------------------------------------------------------------------------
// Schedule
// ---------------------------------------------------------------------------

/** The milestone edit form's values: dates as the date inputs hold them ('' = none). */
export interface MilestoneDraft {
  planned: string;
  forecast: string;
  actual: string;
  gate: boolean;
}

export function milestoneDraftOf(m: ScheduleMilestone): MilestoneDraft {
  return {
    planned: m.plannedDate.slice(0, 10),
    forecast: m.forecastDate?.slice(0, 10) ?? '',
    actual: m.actualDate?.slice(0, 10) ?? '',
    gate: m.isIvvGate,
  };
}

/**
 * The PATCH body for what changed, and nothing else. The planned date only
 * when the caller may change it (a lead) and it is not cleared; forecast and
 * actual clear to null.
 */
export function milestoneChanges(
  m: ScheduleMilestone,
  draft: MilestoneDraft,
  opts: { canEditPlanned: boolean },
): UpdateMilestoneBody {
  const before = milestoneDraftOf(m);
  const body: UpdateMilestoneBody = {};
  if (opts.canEditPlanned && draft.planned !== '' && draft.planned !== before.planned) {
    body.planned_date = draft.planned;
  }
  if (draft.forecast !== before.forecast) body.forecast_date = draft.forecast || null;
  if (draft.actual !== before.actual) body.actual_date = draft.actual || null;
  if (draft.gate !== before.gate) body.is_ivv_gate = draft.gate;
  return body;
}

/** Whether the body moves a date (the API asks an AI-derived milestone for a reason then). */
export function changesADate(body: UpdateMilestoneBody): boolean {
  return 'planned_date' in body || 'forecast_date' in body || 'actual_date' in body;
}

/** The reason floor the API sets on a milestone date override. */
export const MIN_REASON_CHARS = 10;

// ---------------------------------------------------------------------------
// Domain detail
// ---------------------------------------------------------------------------

/** "Rated 7 Oct 2026 by Theo Kessler", or "Not rated yet" ("Scored …" with the verb). */
export function whoWhen(at: string | null, by: string | null, verb = 'Rated'): string {
  return at ? `${verb} ${formatDate(at)}${by ? ` by ${by}` : ''}` : `Not ${verb.toLowerCase()} yet`;
}

/** Whole days since a finding was raised (its age), never below 0. */
export function findingAgeDays(createdAt: string, today: Date): number {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  return Math.max(0, Math.floor((start - dateValue(createdAt.slice(0, 10))) / DAY));
}

/**
 * How a rating answers the AI proposal: the same rating confirms it (the
 * proposal is accepted with the person's reason); another overrides it.
 */
export function ratingMode(
  rating: DomainRating,
  proposal: HrDomain['aiProposal'],
): 'confirm' | 'override' {
  return proposal && proposal.rating === rating ? 'confirm' : 'override';
}

/** The AI reading arrived after the last rating or score (or there is none): it is new. */
export function isNewerThan(createdAt: string | null, ratedAt: string | null): boolean {
  if (!createdAt) return false;
  if (!ratedAt) return true;
  return new Date(createdAt).getTime() > new Date(ratedAt).getTime();
}

// ---------------------------------------------------------------------------
// Criterion detail
// ---------------------------------------------------------------------------

/**
 * The criterion's score over time: each snapshot's frozen score, then
 * today's live score when it is scored and no snapshot was taken today
 * (prototype `criterionDetail()`).
 */
export function criterionTrendPoints(
  criterion: HrCriterion,
  thresholds: Thresholds,
  today: Date,
): TrendPoint[] {
  const sub = (v: number) => ZONE_LABEL[zoneOf(v, thresholds)];
  const points: TrendPoint[] = criterion.trend
    .filter((p) => p.score_percent !== null)
    .map((p) => ({
      date: p.captured_at,
      value: p.score_percent as number,
      sub: sub(p.score_percent as number),
    }));
  const todayIso = localDayIso(today);
  const last = points[points.length - 1];
  const snappedToday = last ? formatDate(last.date) === formatDate(todayIso) : false;
  if (criterion.scorePercent !== null && !snappedToday) {
    points.push({
      date: todayIso,
      value: criterion.scorePercent,
      live: true,
      sub: sub(criterion.scorePercent),
    });
  }
  return points;
}

/** The first snapshot the score has a value at (the "since" of the trend change). */
export function firstScoredSnapshot(criterion: HrCriterion): string | null {
  return criterion.trend.find((p) => p.score_percent !== null)?.captured_at ?? null;
}

const EVIDENCE_KIND_LABEL: Record<string, string> = {
  deliverable: 'Deliverable',
  deviation: 'Deviation',
  finding: 'Finding',
  domain_rating: 'Domain',
};

/** The evidence row's kind word ("Deliverable", "Domain"). */
export function evidenceKindLabel(kind: string): string {
  return EVIDENCE_KIND_LABEL[kind] ?? kind.replace(/_/g, ' ');
}

/** The key a suggested link is kept or dropped by. */
export function linkKey(l: Pick<HrSuggestedLink, 'sourceEntityKind' | 'sourceEntityId'>): string {
  return `${l.sourceEntityKind}:${l.sourceEntityId}`;
}

/**
 * What the confirm sends about the suggestions: the kept ones, and only
 * when the AI suggested any (the API keeps every suggested link it is not
 * told about, and only suggested ones may be kept).
 */
export function keptLinks(
  suggested: ReadonlyArray<HrSuggestedLink>,
  dropped: ReadonlySet<string>,
): { kept_links?: KeptLink[] } {
  if (suggested.length === 0) return {};
  return {
    kept_links: suggested
      .filter((l) => !dropped.has(linkKey(l)))
      .map((l) => ({ source_entity_kind: l.sourceEntityKind, source_entity_id: l.sourceEntityId })),
  };
}

const RATING_STATUS = {
  healthy: 'healthy',
  at_risk: 'at-risk',
  critical: 'critical',
} as const;

/** The word an evidence row shows on its right, and its chip tone. */
export function evidenceState(ref: CrossModuleReferenceItem): {
  text: string;
  tone: StatusChipTone;
} {
  const r = ref.resolved;
  if (r.status === 'missing') return { text: 'missing', tone: 'frozen' };
  if (ref.source_entity_kind === 'domain_rating') {
    const status =
      r.rating && r.rating in RATING_STATUS ? RATING_STATUS[r.rating as DomainRating] : null;
    return status
      ? { text: POSTURE_LABEL[status], tone: status }
      : { text: POSTURE_LABEL.unassessed, tone: 'unassessed' };
  }
  const text = (r.status_value ?? r.severity ?? 'live').replace(/_/g, ' ');
  // The prototype's reading: accepted or resolved reads well; open,
  // outstanding, revise or a breach reads badly; anything else is plain.
  if (/accepted|resolved|closed|approved|met\b/i.test(text)) return { text, tone: 'healthy' };
  if (/open|outstanding|revise|reject|critical|breach|overdue/i.test(text)) {
    return { text, tone: 'critical' };
  }
  return { text, tone: 'neutral' };
}

/** A suggested link's name: the API's label, else the domain it rates, else its kind. */
export function suggestionTitle(link: HrSuggestedLink, domains: ReadonlyArray<HrDomain>): string {
  if (link.label) return link.label;
  if (link.sourceEntityKind === 'domain_rating') {
    const domain = domains.find(
      (d) => d.id === link.sourceEntityId || d.latestAssessmentId === link.sourceEntityId,
    );
    if (domain) return `${domain.name} rating`;
  }
  return `${evidenceKindLabel(link.sourceEntityKind)} ${link.sourceEntityId.slice(0, 8)}`;
}
